import React, { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';

import * as ENDPOINT from 'config/endpoint';
import { getMomentDate, getMomentTime } from 'utils/moment';
import { OrdersContext } from 'pages/Orders/OrdersList';
import { getFullName, getRubles, turnOffEvent } from 'utils/helpers';
import { appStoreMakeRequest } from 'reactStore/slices/appStoreSlice';
import MGrid from 'components/MaterialUI/MGrid';
import FormInputDisplay from 'components/FormElements/FormInputDisplay';
import {
  changeOrderItemStateForCyrillic,
  changeOrderStateForCyrillic,
  changePassportTypeForCyrillic,
} from 'pages/Orders/helpers';
import { checkAvailableAmount } from 'reactStore/selectors/balanceSelector';
import MText from 'components/MaterialUI/MText';
import DocumentOptions from 'pages/Orders/AviaOrders/AviaOrderPassengers/DocumentOptions';
import MToggleIconButton from 'components/MaterialUI/MToggleIconButton';
import CancelPopover from 'pages/Orders/AviaOrders/AviaOrderPassengers/CancelPopover';
import { getUserDataSelector } from 'reactStore/selectors/userSelector';
import { RegularWrapper, Text, Divider } from 'components/common';
import RulesDialog from '../AviaOrderWays/RulesDialog';
import { CardWrapper, Circle } from './OrderPassengerCard.styles';

const OrderPassengerCard = (props) => {
  const {
    contractId,
    index,
    orderId,
    passenger,
    passenger: { reservation = {}, traveller = {}, document = {} },
    ownerId,
  } = props;
  const user = useSelector(getUserDataSelector, shallowEqual);
  const isPersonEqualTraveller =
    (ownerId === traveller?.person_id && user?.role === 'self_booker') || user?.role === 'manager';

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [disabledCancelBtn, setDisabledCancelBtn] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { storeName } = useContext(OrdersContext);
  const mapper = useCallback((res, old) => old.map((i) => (i['id'] !== orderId ? i : res.data.data)), [orderId]);

  const allowCancel = ['booked', 'voided', 'confirmed'].includes(reservation['state']) && isPersonEqualTraveller;
  const allowConfirm = ['booked'].includes(reservation['state']) && isPersonEqualTraveller;

  const actualPrice = reservation?.actual_sell_price?.provider_total?.gross?.cents || 0;
  const totalOrderAmount = reservation?.actual_sell_price?.order_item_total?.gross?.cents || 0;
  const originalPrice = reservation?.initial_sell_price?.provider_total?.gross?.cents || 0;
  const serviceFee = reservation?.actual_sell_price?.client_fees_total?.gross?.cents || 0;

  const isRefund = !!reservation['direction']?.['legs']?.[0]?.['segments']?.[0]?.['rules']?.['refundable'];
  const isAvailableAmount = useSelector(checkAvailableAmount(contractId), shallowEqual);

  const handleCancel = (e) => {
    turnOffEvent(e);
    if (reservation['state'] === 'confirmed') {
      setDisabledCancelBtn(true);
      setAnchorEl(e.currentTarget);
    } else {
      const dataSource = `${ENDPOINT.ORDERS}/${orderId}/cancel`;
      const data = { data: { avia: { order_items: [{ id: passenger['id'] }] } } };
      dispatch(appStoreMakeRequest({ storeName, dataSource, data, mapper, method: 'PUT' }));
      setDisabledCancelBtn(false);
    }
  };

  const handleConfirm = () => {
    const dataSource = `${ENDPOINT.ORDERS}/${orderId}/confirm`;
    const data = { ['data']: { ['avia']: { ['order_items']: [{ ['id']: passenger['id'] }] } } };
    dispatch(appStoreMakeRequest({ storeName, dataSource, data, mapper, method: 'PUT' }));
  };

  const renderReservation = () => {
    const { state, direction, ['expires_in']: expires, ['cancelled_at']: canceled } = reservation;
    const segment = direction?.['legs'].map((leg) => leg?.['segments']?.[0]);
    const confirmedValue = segment.map((item) => (
      <RegularWrapper width={'150px'} flow={'column'}>
        <MText
          weight={'bold'}
          variant={'subtitle2'}
          children={`${item?.['class_type']?.['name']} (${item?.['class_type']?.['service']})`}
        />
        <MText variant={'subtitle2'} children={<RulesDialog {...{ orderId }} />} />
      </RegularWrapper>
    ));

    switch (state) {
      case 'booked':
        return <FormInputDisplay label="Выписать до" value={getMomentDate(expires)} />;
      case 'confirmed':
        return (
          <FormInputDisplay label="Класс и Тариф" value={<MGrid direction={'column'} children={confirmedValue} />} />
        );
      case 'voided':
      case 'cancelled':
        return <FormInputDisplay label="Дата отмены" value={getMomentDate(canceled)} />;
      default:
        return null;
    }
  };

  const timeExpiresIn = () => {
    return `${getMomentDate(reservation?.['expires_in'])} 
    ${getMomentTime(reservation?.['expires_in'])}, GMT + ${parseInt(reservation?.['expires_in']?.split('+')[1])}`;
  };

  const reservationCodeWithRoad = () => {
    const arrAirp = reservation?.['direction']?.['legs']?.[0]?.['arr_airp']?.['city'];
    const depAirp = reservation?.['direction']?.['legs']?.[0]?.['dep_airp']?.['city'];

    return `${depAirp} - ${arrAirp}: ${reservation['vnd_pnrs']?.join(', ')}`;
  };

  return (
    <RegularWrapper margin={'12px 0 0 0'}>
      <Circle open={open}>{index}</Circle>
      <CardWrapper open={open || Boolean(anchorEl)} padding={'10px 20px 20px 15px'} radius={'5px'} flow={'column'}>
        <RegularWrapper>
          <Text width={'70%'} size={'14px'} fontWeight={'bold'} color={'#3D3F43'} children={getFullName(traveller)} />

          <RegularWrapper>
            <FormInputDisplay
              size={4}
              label={'OVT бронь'}
              value={changeOrderItemStateForCyrillic(passenger['state'])}
              code={passenger['code']}
            />
            <FormInputDisplay sx={{ width: '85px' }} size={4} label={'Выписать до'} value={timeExpiresIn()} />
            <FormInputDisplay
              color={'#31AE54'}
              size={4}
              label={'Статус билета'}
              value={changeOrderStateForCyrillic(reservation['state'])}
            />
          </RegularWrapper>

          <RegularWrapper justify={'flex-end'} align={'center'}>
            {allowCancel && (
              <Button
                style={{
                  marginRight: 7,
                  background: open ? '#fff' : '#F2F4F6',
                  color: '#71757F',
                  border: open ? '1px solid #fff' : '1px solid #F2F4F6',
                }}
                size="small"
                variant="outlined"
                disabled={disabledCancelBtn}
                onClick={handleCancel}
                children={t('Actions.Cancel')}
              />
            )}
            {allowConfirm && (
              <Button
                size="small"
                style={{
                  background: open ? '#fff' : '#EBF7F6',
                  color: '#35A9A2',
                  border: open ? '1px solid #fff' : '1px solid #EBF7F6',
                }}
                variant="contained"
                onClick={handleConfirm}
                disabled={!isAvailableAmount}
                children={t('Actions.Confirm')}
              />
            )}
            <DocumentOptions {...{ passenger, open }} />
            <MToggleIconButton {...{ open, setOpen }} />
          </RegularWrapper>
        </RegularWrapper>
        {open && (
          <RegularWrapper flow={'column'}>
            <Divider height="2px" bgColor={'#E3F2FD'} margin={'15px 0 20px 0'} />

            <RegularWrapper>
              <FormInputDisplay size={1} label={t('Orders.BookingCode')} value={reservation['pnr']} />
              <RegularWrapper width={'345px'}>
                <FormInputDisplay label={t('Orders.ReservationCode')} value={reservationCodeWithRoad()} />
              </RegularWrapper>
              <RegularWrapper width={'90px'} margin={'0 40px 0 0'}>
                <FormInputDisplay label={t('Orders.TicketNumber')} value={reservation['ticket_number']} />{' '}
              </RegularWrapper>
              <RegularWrapper width={'90px'}>{renderReservation()}</RegularWrapper>
            </RegularWrapper>

            <RegularWrapper>
              <RegularWrapper width={'120px'} margin={'28px 0 0 0'} flow={'column'}>
                <FormInputDisplay label={t('Users.Birthday')} value={getMomentDate(traveller['date_of_birth'])} />
                <FormInputDisplay
                  label={t('Orders.OriginalPrice')}
                  color={'#999ea6'}
                  margin={'28px 0 0 0'}
                  value={getRubles(originalPrice)}
                />
                <FormInputDisplay margin={'28px 0 0 0'} label={'ЦФО'} value="-" />
              </RegularWrapper>

              <FormInputDisplay
                margin={'28px 0 0 0'}
                label={t('Users.Gender')}
                value={traveller['gender'] === 'F' ? 'Жен.' : 'Муж.'}
              />

              <RegularWrapper width={'110px'} margin={'28px 0 0 22px'} flow={'column'}>
                <FormInputDisplay label={t('Users.Nationality')} value={traveller['nationality']?.['text']} />
                <FormInputDisplay
                  margin={'28px 0 0 0'}
                  label={t('Orders.ActualPrice')}
                  value={getRubles(actualPrice)}
                />
                <FormInputDisplay margin={'28px 0 0 0'} label={'Тревел-политика'} value="-" />
              </RegularWrapper>

              <RegularWrapper width={'130px'} margin={'28px 0 0 22px'} flow={'column'}>
                <FormInputDisplay
                  label={changePassportTypeForCyrillic(traveller['document']?.['_type'])}
                  sx={{ color: '#3D3F43' }}
                  value={traveller['document']?.['number']}
                />
                <FormInputDisplay margin={'28px 0 0 0'} label={t('Orders.RefundAmount')} value="-" />

                <FormInputDisplay margin={'28px 0 0 0'} label={t('Offices.Department')} value="-" />
              </RegularWrapper>

              <RegularWrapper width={'130px'} margin={'28px 0 0 0'} flow={'column'}>
                <FormInputDisplay
                  label={t('Users.PassportExpiredAt')}
                  value={getMomentDate(document['elapsed_time'])}
                />
                <FormInputDisplay margin={'28px 0 0 0'} label={t('Orders.ServiceFee')} value={getRubles(serviceFee)} />

                <FormInputDisplay margin={'28px 0 0 0'} label={t('Offices.Office')} value="-" />
              </RegularWrapper>

              <RegularWrapper width={'130px'} margin={'28px 0 0 0'} flow={'column'}>
                <FormInputDisplay label={'Карта лояльности'} value={'-'} />

                <FormInputDisplay
                  margin={'28px 0 0 0'}
                  label={t('Orders.TotalCost')}
                  weight={'bolder'}
                  value={getRubles(totalOrderAmount)}
                />
                <FormInputDisplay margin={'28px 0 0 0'} label={'Номер счета'} value="-" />
              </RegularWrapper>
              <RegularWrapper width={'130px'} margin={'28px 0 0 0'} justify={'space-between'} flow={'column'}>
                <FormInputDisplay sx={{ color: '#3D3F43' }} label={traveller['phone']} value={traveller['email']} />
                <FormInputDisplay margin={'4px 0 0 0'} label={'Оплачено'} value="-" />
              </RegularWrapper>
            </RegularWrapper>
          </RegularWrapper>
        )}
      </CardWrapper>

      <CancelPopover
        {...{ anchorEl, isRefund, passenger, mapper, setDisabledCancelBtn }}
        onClose={() => setAnchorEl(null)}
      />
    </RegularWrapper>
  );
};

OrderPassengerCard.propTypes = {
  orderId: PropTypes.any,
  index: PropTypes.number,
  contractId: PropTypes.string,
  passenger: PropTypes.object,
  ownerId: PropTypes.string,
};

OrderPassengerCard.defaultProps = {
  passenger: {},
};

export default OrderPassengerCard;
